import { Box } from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney, INCOME_MODE_HELP, INCOME_MODE_LABELS } from '../../../library/dashboardDisplay';
import { ITherapistOwnBreakdown } from '../../../library/models/therapistDashboard';
import ResourcesIds from '../../../resources/ResourcesIds';
import { KpiTile } from '../../dashboard/KpiTile';
import { kpiGridStyles } from '../../dashboard/kpiTile.styles';

interface TherapistIncomeKpiRowProps {
    totals: ITherapistOwnBreakdown;
}

/**
 * A therapist's headline figures, with the two money tiles side by side.
 *
 * The clinic dashboard makes billed and earned a toggle, because an administrator reading
 * a table of colleagues wants one column at a time. Here they are both tiles at once:
 * the question this page exists to answer is the gap between them, and a gap you have to
 * flip a switch to see is not on screen.
 *
 * Both money tiles are accented, and nothing else is -- an exception to the usual "at most
 * one accent" rule, and a deliberate one: the pair is the page, and accenting only the
 * first would rank them when they are meant to be read against each other.
 *
 * They borrow the clinic page's own labels and help text (`INCOME_MODE_*`) rather than
 * defining their own, so the same figure is never called two things in one product.
 *
 * Six tiles, which is what the shared grid divides evenly at every breakpoint.
 */
export const TherapistIncomeKpiRow = ({ totals }: TherapistIncomeKpiRowProps): JSX.Element => {
    const { t: translate } = useTranslation();

    return (
        <Box sx={kpiGridStyles}>
            <KpiTile
                label={translate(INCOME_MODE_LABELS.BILLED)}
                value={formatMoney(totals.billed, translate)}
                caption={translate(ResourcesIds.THERAPIST_INCOME_BILLED_CAPTION)}
                help={translate(INCOME_MODE_HELP.BILLED)}
                accented
                testId="therapist-kpi-billed"
            />

            <KpiTile
                label={translate(INCOME_MODE_LABELS.EARNED)}
                value={formatMoney(totals.earned, translate)}
                caption={translate(ResourcesIds.THERAPIST_INCOME_EARNED_CAPTION)}
                help={translate(INCOME_MODE_HELP.EARNED)}
                accented
                testId="therapist-kpi-earned"
            />

            <KpiTile
                label={translate(ResourcesIds.DASHBOARD_KPI_PURCHASES_SOLD)}
                value={String(totals.purchasesSold)}
                testId="therapist-kpi-purchases-sold"
            />

            <KpiTile
                label={translate(ResourcesIds.DASHBOARD_KPI_SESSIONS_HELD)}
                value={String(totals.sessionsHeld)}
                testId="therapist-kpi-sessions-held"
            />

            <KpiTile
                label={translate(ResourcesIds.DASHBOARD_KPI_NEW_PATIENTS)}
                value={String(totals.newPatients)}
                testId="therapist-kpi-new-patients"
            />

            <KpiTile
                label={translate(ResourcesIds.DASHBOARD_KPI_RETURNING_PATIENTS)}
                value={String(totals.existingPatients)}
                testId="therapist-kpi-existing-patients"
            />
        </Box>
    );
};
